import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit, Trash2, List, LayoutGrid } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api } from '@/services/api';
import { toast } from 'sonner';
import { useConfirm } from '@/hooks/useConfirm';

const Clients = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [viewMode, setViewMode] = useState('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const { confirm, ConfirmDialog } = useConfirm();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    neighborhood: '',
    city: '',
    notes: '',
  });

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const filtered = clients.filter(
        (client) =>
          client.name.toLowerCase().includes(term) ||
          client.phone.includes(term) ||
          (client.email && client.email.toLowerCase().includes(term))
      );
      setFilteredClients(filtered);
    } else {
      setFilteredClients(clients);
    }
    setCurrentPage(1); // Reset to first page when search changes
  }, [searchTerm, clients]);

  const loadClients = async () => {
    try {
      setLoading(true);
      const response = await api.getClients();
      setClients(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar campos obligatorios
    if (!formData.name || !formData.phone) {
      toast.error('Por favor completa todos los campos obligatorios (*)');
      return;
    }
    
    // Limpiar datos: enviar null para campos opcionales vacíos
    const dataToSend = {
      name: formData.name,
      phone: formData.phone,
      email: formData.email?.trim() || null,
      address: formData.address?.trim() || null,
      neighborhood: formData.neighborhood?.trim() || null,
      city: formData.city?.trim() || null,
      notes: formData.notes?.trim() || null
    };
    
    try {
      if (editingClient) {
        await api.updateClient(editingClient.id, dataToSend);
        toast.success('Cliente actualizado');
      } else {
        await api.createClient(dataToSend);
        toast.success('Cliente creado');
      }
      setShowModal(false);
      resetForm();
      loadClients();
    } catch (error) {
      console.error('Error al guardar cliente:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Error al guardar cliente');
    }
  };

  const handleEdit = (client) => {
    setEditingClient(client);
    setFormData({
      name: client.name || '',
      phone: client.phone || '',
      email: client.email || '',
      address: client.address || '',
      neighborhood: client.neighborhood || '',
      city: client.city || '',
      notes: client.notes || '',
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm('Eliminar Cliente', '¿Estás seguro de eliminar este cliente? Esta acción no se puede deshacer.');
    if (!confirmed) return;
    try {
      await api.deleteClient(id);
      toast.success('Cliente eliminado');
      loadClients();
    } catch (error) {
      toast.error('Error al eliminar cliente');
    }
  };

  const resetForm = () => {
    setEditingClient(null);
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      neighborhood: '',
      city: '',
      notes: '',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-[#C9A961] text-xl font-cormorant">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="clients-page">
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-cormorant font-bold text-[#2C2C2C]">Clientes</h1>
            <p className="text-[#6B6B6B] mt-1">Gestiona tu base de clientes</p>
          </div>

          <div className="flex space-x-2">
            <div className="flex border border-[#C9A961]/30 rounded">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className={viewMode === 'list' ? 'bg-[#C9A961]' : ''}
              >
                <List size={16} />
              </Button>
              <Button
                variant={viewMode === 'cards' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('cards')}
                className={viewMode === 'cards' ? 'bg-[#C9A961]' : ''}
              >
                <LayoutGrid size={16} />
              </Button>
            </div>
            <Button
              onClick={() => setShowImportModal(true)}
              variant="outline"
              className="border-[#C9A961]/30 hover:border-[#C9A961]"
            >
              Importar Excel
            </Button>
            <Button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              data-testid="create-client-button"
              className="bg-[#C9A961] hover:bg-[#B89551]"
            >
              <Plus className="mr-2" size={20} />
              Nuevo Cliente
            </Button>
          </div>
        </div>

        <div className="mt-6 relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6B6B6B]" size={20} />
          <Input
            placeholder="Buscar por nombre, teléfono o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-clients-input"
            className="pl-10 border-[#C9A961]/30"
          />
        </div>
      </div>

      {viewMode === 'list' ? (
        <>
          <div className="border border-[#C9A961]/20 rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Teléfono</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Ciudad</TableHead>
                  <TableHead>Visitas</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map((client) => (
                <TableRow key={client.id}>
                  <TableCell className="font-medium">{client.name}</TableCell>
                  <TableCell>{client.phone}</TableCell>
                  <TableCell>{client.email || '-'}</TableCell>
                  <TableCell>{client.city || '-'}</TableCell>
                  <TableCell>
                    <span className="px-2 py-1 text-xs rounded bg-[#C9A961]/10 text-[#C9A961] font-semibold">
                      {client.total_visits || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(client)}
                        data-testid={`edit-client-${client.id}`}
                        className="border-[#C9A961]/30 hover:border-[#C9A961]"
                      >
                        <Edit size={16} />
                      </Button>
                      {user.role === 'admin' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDelete(client.id)}
                          data-testid={`delete-client-${client.id}`}
                          className="border-red-300 hover:border-red-500 text-red-500"
                        >
                          <Trash2 size={16} />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredClients.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-[#6B6B6B] py-8">
                    No se encontraron clientes
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </div>
          
          {/* Pagination Controls */}
          {filteredClients.length > itemsPerPage && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-[#6B6B6B]">
                Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, filteredClients.length)} - {Math.min(currentPage * itemsPerPage, filteredClients.length)} de {filteredClients.length} clientes
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="border-[#C9A961]/30"
                >
                  Anterior
                </Button>
                <span className="px-3 py-2 text-sm">
                  Página {currentPage} de {Math.ceil(filteredClients.length / itemsPerPage)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage >= Math.ceil(filteredClients.length / itemsPerPage)}
                  className="border-[#C9A961]/30"
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredClients
              .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
              .map((client) => (
          <Card
            key={client.id}
            className="p-5 hover:shadow-lg transition-shadow border-[#C9A961]/20 card-hover"
            data-testid={`client-card-${client.id}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-[#2C2C2C]">{client.name}</h3>
                <p className="text-sm text-[#6B6B6B] mt-1">{client.phone}</p>
                {client.email && <p className="text-sm text-[#6B6B6B]">{client.email}</p>}
              </div>
              <div className="w-12 h-12 rounded-full bg-[#C9A961] flex items-center justify-center text-white font-semibold text-lg">
                {client.name.charAt(0).toUpperCase()}
              </div>
            </div>

            {(client.address || client.city) && (
              <div className="mb-3 text-sm text-[#6B6B6B]">
                {client.address && <p>{client.address}</p>}
                {client.neighborhood && client.city && (
                  <p>
                    {client.neighborhood}, {client.city}
                  </p>
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-[#C9A961]/20">
              <div className="text-sm">
                <span className="text-[#6B6B6B]">Visitas: </span>
                <span className="font-semibold text-[#C9A961]">{client.total_visits || 0}</span>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(client)}
                  data-testid={`edit-client-${client.id}`}
                  className="border-[#C9A961]/30 hover:border-[#C9A961]"
                >
                  <Edit size={16} />
                </Button>
                {user.role === 'admin' && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(client.id)}
                    data-testid={`delete-client-${client.id}`}
                    className="border-red-300 hover:border-red-500 text-red-500"
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            </div>
          </Card>
        ))}
        </div>
        
        {/* Pagination Controls for Cards View */}
        {filteredClients.length > itemsPerPage && (
          <div className="flex items-center justify-between mt-4">
            <div className="text-sm text-[#6B6B6B]">
              Mostrando {Math.min((currentPage - 1) * itemsPerPage + 1, filteredClients.length)} - {Math.min(currentPage * itemsPerPage, filteredClients.length)} de {filteredClients.length} clientes
            </div>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="border-[#C9A961]/30"
              >
                Anterior
              </Button>
              <span className="px-3 py-2 text-sm">
                Página {currentPage} de {Math.ceil(filteredClients.length / itemsPerPage)}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={currentPage >= Math.ceil(filteredClients.length / itemsPerPage)}
                className="border-[#C9A961]/30"
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </>
      )}

      {filteredClients.length === 0 && viewMode === 'cards' && (
        <div className="text-center py-12">
          <p className="text-[#6B6B6B]">No se encontraron clientes</p>
        </div>
      )}

      {/* Client Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl" data-testid="client-modal">
          <DialogHeader>
            <DialogTitle className="text-2xl font-cormorant font-bold text-[#C9A961]">
              {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-[#2C2C2C] font-medium">Nombre Completo *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="client-name-input"
                  className="mt-1.5"
                  required
                />
              </div>

              <div>
                <Label className="text-[#2C2C2C] font-medium">Teléfono *</Label>
                <Input
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  data-testid="client-phone-input"
                  className="mt-1.5"
                  required
                />
              </div>

              <div>
                <Label className="text-[#2C2C2C] font-medium">Email</Label>
                <Input
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="mt-1.5"
                  placeholder="email@ejemplo.com"
                />
              </div>

              <div>
                <Label className="text-[#2C2C2C] font-medium">Ciudad</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label className="text-[#2C2C2C] font-medium">Dirección</Label>
                <Input
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label className="text-[#2C2C2C] font-medium">Barrio</Label>
                <Input
                  value={formData.neighborhood}
                  onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                  className="mt-1.5"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                Cancelar
              </Button>
              <Button type="submit" className="bg-[#C9A961] hover:bg-[#B89551]">
                {editingClient ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Import Excel Modal */}
      <Dialog open={showImportModal} onOpenChange={setShowImportModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-semibold text-[#C9A961]">
              Importar Clientes desde Excel
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="bg-[#F5F2ED] p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Formato del Excel:</h3>
              <p className="text-sm text-[#6B6B6B] mb-2">El archivo debe tener las siguientes columnas:</p>
              <ul className="text-sm text-[#6B6B6B] list-disc list-inside space-y-1">
                <li><strong>name</strong> - Nombre completo (requerido)</li>
                <li><strong>phone</strong> - Teléfono (requerido)</li>
                <li><strong>email</strong> - Email (opcional)</li>
                <li><strong>address</strong> - Dirección (opcional)</li>
                <li><strong>neighborhood</strong> - Barrio (opcional)</li>
                <li><strong>city</strong> - Ciudad (opcional)</li>
              </ul>
            </div>
            <div>
              <Label>Selecciona archivo Excel (.xlsx, .csv)</Label>
              <Input
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  
                  // Para CSV simple
                  if (file.name.endsWith('.csv')) {
                    const text = await file.text();
                    const lines = text.split('\n');
                    const headers = lines[0].split(',').map(h => h.trim());
                    
                    for (let i = 1; i < lines.length; i++) {
                      if (!lines[i].trim()) continue;
                      const values = lines[i].split(',').map(v => v.trim());
                      const client = {};
                      headers.forEach((header, idx) => {
                        client[header] = values[idx] || '';
                      });
                      
                      if (client.name && client.phone) {
                        try {
                          await api.createClient(client);
                        } catch (error) {
                          console.error('Error importing client:', error);
                        }
                      }
                    }
                    toast.success('Clientes importados');
                    setShowImportModal(false);
                    loadClients();
                  } else {
                    toast.error('Por favor usa formato CSV por ahora');
                  }
                }}
                className="mt-1.5"
              />
            </div>
            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setShowImportModal(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog />
    </div>
  );
};

export default Clients;